import * as React from "react";
import { useDispatch } from "react-redux";

import { Card, Divider, makeStyles } from "@material-ui/core";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";

import AddIcon from "@material-ui/icons/Add";
import SaveIcon from "@material-ui/icons/Save";

import "react-perfect-scrollbar/dist/css/styles.css";
import PerfectScrollbar from "react-perfect-scrollbar";

import {
  PageHeader,
  WithReduxForm,
  ReferenceInput,
  AutoCompleteInput,
  TextInput,
  NumberInput,
  SelectInput,
  Button,
  BackButton,
  showNotification
} from "jazasoft";
import { SimpleForm } from "jazasoft/lib/mui/form/SimpleForm";

import Table, { NativeTextInput } from "../../../components/Table";
import CardHeader from "../../../components/CardHeader";

import { defaultParsePaste, getDistinctValues, inputOptions, isEmpty, isTrue } from "../../../utils/helpers";
import { CSV, FabricDirection } from "../../../utils/types";
import { REGEX_POSITIVE_NUMBER } from "../../../utils/regex";
import { useDeepEffect, useMutation, useQuery, useSafeState } from "../../../hooks";

const NON_SB_FIELDS = ["label"];

const readSizeList = (rows, haveSizeGroup, columns) => {
  const groupRow = haveSizeGroup ? rows[0] : {}; // Grouped Cut Plan Row, if grouped cut plan
  const dataIdx = haveSizeGroup ? 2 : 1;
  const header = haveSizeGroup ? rows[1] : rows[0];
  const { label, ...sizes } = header;
  const sizekeys = columns.filter(c => !NON_SB_FIELDS.includes(c.dataKey)).map(c => c.dataKey);
  const consumptionRow = rows[dataIdx];

  let sizeGroup;
  let sizeList = [];
  for (let i = 0; i < sizekeys.length; i++) {
    const sizeKey = sizekeys[i];

    sizeGroup = haveSizeGroup && groupRow[sizeKey] ? groupRow[sizeKey] : sizeGroup;
    sizeList.push({
      serialNo: parseInt(sizeKey.substring(4)),
      sizeGroup,
      size: sizes[sizeKey],
      consumption: !isEmpty(consumptionRow[sizeKey]) ? parseFloat(consumptionRow[sizeKey]) : 0
    });
  }

  sizeList = sizeList.filter(e => !isEmpty(e.size) && !isEmpty(e.consumption));
  return sizeList;
};

const useStyles = makeStyles({
  content: {
    margin: "0 1.5em",
    marginBottom: "2em"
  },
  mrTop: {
    marginTop: "1.5em"
  },
  table: {
    "& th, & td": {
      border: "1px solid rgba(224, 224, 224, 1)"
    }
  },
  rowCell: {
    padding: 0,
    fontSize: 12,
    "&:last-child": {
      padding: 0,
      fontSize: 12
    }
  }
});

const SizeChartCreate = props => {
  const { id, history, basePath } = props;
  const classes = useStyles();

  const [sizeBreakupState, setSizeBreakupState] = useSafeState({ columns: [], rows: [], sizeCount: 10, haveSizeGroup: false });

  const [mutate] = useMutation();
  const dispatch = useDispatch();

  const { data: sizeChart = {} } = useQuery({ url: `size-chart/${id}` });

  useDeepEffect(() => {
    if (isEmpty(sizeChart)) return;
    const sizeList = sizeChart?.sizeList?.sort((a, b) => a.serialNo - b.serialNo) || [];
    const sizeCount = sizeList.length > 10 ? sizeList.length : 10;
    const columns = [
      { dataKey: "label", title: "Label" },
      ...Array(sizeCount)
        .fill("")
        .map((_, idx) => ({
          dataKey: `size${idx + 1}`,
          title: `Size ${idx + 1}`,
          align: "center",
          element: <NativeTextInput style={{ textAlign: "center" }} onChange={onChange} onPaste={onPaste} />
        }))
    ];

    const haveSizeGroup = getDistinctValues(sizeList.map(e => e.sizeGroup))?.length > 1;
    let groupRow = { label: "Size Group -->" };
    if (haveSizeGroup) {
      for (let i = 0; i < sizeList.length; i++) {
        if (i === 0 || sizeList[i - 1].sizeGroup !== sizeList[i].sizeGroup) {
          groupRow = { ...groupRow, [`size${i + 1}`]: sizeList[i].sizeGroup };
        }
      }
    }
    const sizeRow = sizeList.reduce((acc, e, idx) => ({ ...acc, [`size${idx + 1}`]: e.size }), { label: "Size -->" });
    const consumptionRow = sizeList.reduce((acc, e, idx) => ({ ...acc, [`size${idx + 1}`]: e.consumption }), {});

    let rows = [];
    if (haveSizeGroup) {
      rows = [groupRow, sizeRow, consumptionRow];
    } else {
      rows = [sizeRow, consumptionRow];
    }

    setSizeBreakupState(prev => ({ sizeCount, haveSizeGroup, columns, rows }));
  }, [sizeChart]);

  const onSubmit = values => {
    const sizeList = readSizeList(sizeBreakupState.rows, sizeBreakupState.haveSizeGroup, sizeBreakupState.columns);
    const data = { ...values, sizeList };
    const options = { url: `size-chart/${id}`, method: "put", data };
    mutate(options, {
      onSuccess: () => {
        dispatch(showNotification(`Size Chart updated successfully`, { type: "success" }));
        history.push(basePath);
      }
    });
  };

  const onPaste =
    ({ rowIdx, colIdx }) =>
    e => {
      e.preventDefault();
      const text = e.clipboardData && e.clipboardData.getData("text");
      const data = defaultParsePaste(text);

      setSizeBreakupState(prev => {
        //** Add Columns if data is more than existing columns **//
        const sizeStartIdx = (prev.columns || []).findIndex(c => c.dataKey.startsWith("size"));
        const colCount = data.sort((a, b) => b.length - a.length)[0].length;
        const finalColCount = colIdx - sizeStartIdx + colCount > prev.sizeCount ? colIdx - sizeStartIdx + colCount - prev.sizeCount : 0;
        let newColumns = [...prev.columns];
        let sizeCount = prev.sizeCount;
        if (finalColCount > 1) {
          const sizes = Array(finalColCount)
            .fill("")
            .map((_, idx) => ({
              dataKey: `size${sizeCount + idx + 1}`,
              title: `Size ${sizeCount + idx + 1}`,
              align: "center",
              element: <NativeTextInput style={{ textAlign: "center" }} onChange={onChange} onPaste={onPaste} />
            }));

          newColumns = [...prev.columns, ...sizes];
          sizeCount += finalColCount;
        }

        //** Process Rows data*//
        let newRows = [...prev.rows];
        for (let i = rowIdx; i < data.length + rowIdx && i < newRows?.length; i++) {
          const row = data[i - rowIdx];
          let newRow = newRows[i];
          if (row && newRow) {
            for (let j = colIdx; j < row.length + colIdx; j++) {
              const column = newColumns[j] || {};
              const sizeKey = !isEmpty(column.dataKey) ? column.dataKey : `size${j}`;
              const value = row[j - colIdx];
              const sizeRowIdx = prev.haveSizeGroup ? 1 : 0;
              const allowInput =
                i > sizeRowIdx && (isEmpty(column) || column.dataKey.includes("size"))
                  ? !isNaN(String(value)) || isEmpty(value) || REGEX_POSITIVE_NUMBER.test(value)
                  : true;
              if (allowInput) {
                newRow = { ...newRow, [sizeKey]: value };
              }
            }
          }

          newRows.splice(i, 1, newRow);
        }
        return { ...prev, rows: [...newRows], columns: [...newColumns], sizeCount };
      });
    };

  const onChange =
    ({ rowIdx, column }) =>
    e => {
      const value = e?.target?.value || e?.value;
      setSizeBreakupState(prev => {
        let newRows = [...prev.rows];
        let row;
        const data = defaultParsePaste(value) || [];
        if (data.length === 0 || (data.length === 1 && data[0].length === 1)) {
          // Restricting input of String value in size rows
          const sizeRowIdx = prev.haveSizeGroup ? 1 : 0;
          let allowInput =
            rowIdx > sizeRowIdx && column.dataKey.includes("size")
              ? !isNaN(String(value)) || isEmpty(value) || REGEX_POSITIVE_NUMBER.test(value)
              : true;
          if (allowInput) {
            row = { ...prev.rows[rowIdx], [column.dataKey]: value };
            newRows.splice(rowIdx, 1, row);
          }
        }
        return { ...prev, rows: newRows };
      });
    };

  const onAddColumn = colCount => {
    setSizeBreakupState(({ columns, sizeCount, ...prev }) => {
      const sizes = Array(colCount)
        .fill("")
        .map((_, idx) => ({
          dataKey: `size${sizeCount + idx + 1}`,
          title: `Size ${sizeCount + idx + 1}`,
          align: "center",
          element: <NativeTextInput style={{ textAlign: "center" }} onChange={onChange} onPaste={onPaste} />
        }));

      let newColumns = [...columns, ...sizes];
      return { ...prev, columns: [...newColumns], sizeCount: sizeCount + colCount };
    });
  };

  const onToggle = () => {
    setSizeBreakupState(prev => {
      const haveSizeGroup = isTrue(prev.haveSizeGroup) ? false : true;

      let rowList = prev.rows;
      if (haveSizeGroup) {
        rowList = [{ label: "Size Group -->" }, ...prev.rows];
      } else {
        rowList = prev.rows.filter(row => row.label !== "Size Group -->");
      }
      return { ...prev, rows: rowList, haveSizeGroup };
    });
  };

  return (
    <div>
      <PageHeader title="Size Chart" />

      <div className={classes.content}>
        <WithReduxForm initialValues={{ ...sizeChart, customerId: sizeChart?.customer?.id, productId: sizeChart?.product?.id }}>
          {({ handleSubmit }) => (
            <div>
              <Card>
                <CardHeader title="Basic Info" />
                <Divider />
                <SimpleForm component="div" footer={false} style={{ padding: "0 1.5em" }}>
                  <ReferenceInput source="customerId" reference="customers" {...inputOptions(3)}>
                    <AutoCompleteInput />
                  </ReferenceInput>
                  <ReferenceInput source="productId" reference="products" {...inputOptions(3)}>
                    <AutoCompleteInput />
                  </ReferenceInput>
                  <TextInput source="style" {...inputOptions(3)} />
                  <TextInput source="fit" {...inputOptions(3)} />
                  <NumberInput source="fabricWidth" {...inputOptions(3)} />
                  <ReferenceInput source="fabricPatternId" reference="fabric-patterns" {...inputOptions(3)}>
                    <AutoCompleteInput />
                  </ReferenceInput>
                  <NumberInput source="repeatLength" {...inputOptions(3)} />
                  <NumberInput source="repeatWidth" {...inputOptions(3)} />
                  <SelectInput
                    source="fabricDirection"
                    choices={Object.keys(FabricDirection).map(key => ({ id: FabricDirection[key], name: FabricDirection[key] }))}
                    {...inputOptions(3)}
                  />
                  <SelectInput source="csv" choices={Object.keys(CSV).map(key => ({ id: CSV[key], name: CSV[key] }))} {...inputOptions(3)} />
                </SimpleForm>
              </Card>

              <Card className={classes.mrTop}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingRight: "0.5em" }}>
                  <CardHeader title="Size Chart" />
                  <div>
                    <FormControlLabel
                      control={<Switch checked={isTrue(sizeBreakupState.haveSizeGroup)} onChange={onToggle} />}
                      label="Have Size Group?"
                    />
                    <Button variant="outlined" showLabel={false} label="Add Columns" onClick={() => onAddColumn(3)}>
                      <AddIcon />
                    </Button>
                  </div>
                </div>
                <PerfectScrollbar>
                  <Table
                    classes={{ table: classes.table, rowCell: classes.rowCell }}
                    columns={sizeBreakupState.columns || []}
                    rows={sizeBreakupState.rows || []}
                    i18nKey="resources.orders.fields.flowInfo"
                    cellStyle={({ column, record }) => ({
                      minWidth: NON_SB_FIELDS.includes(column?.dataKey) ? 120 : undefined,
                      paddingLeft: NON_SB_FIELDS.includes(column?.dataKey) ? 10 : undefined
                    })}
                  />
                </PerfectScrollbar>
              </Card>

              <div style={{ padding: "1.5em 0" }}>
                <Button label="Save" variant="contained" color="primary" onClick={handleSubmit(onSubmit)}>
                  <SaveIcon />
                </Button>
                <BackButton variant="outlined" color="primary" style={{ marginLeft: "1em" }} />
              </div>
            </div>
          )}
        </WithReduxForm>
      </div>
    </div>
  );
};

export default SizeChartCreate;
